import { CommonButton } from 'components/Buttons';
import React from 'react';
import utils from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { actionType, modalActionType } from 'utils/constants';
import selectors from 'state/selectors';
import services from 'services';
import useCheckBeforeAction from '../hooks';
import { lucyModuleKey } from 'utils/configs';
import { useFormikContext } from 'formik';
import { FormValues, Q1Value } from 'models/documents';
import actions from 'state/actions';
import { useHistory } from 'react-router';

const ValidateLease = () => {
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const t = utils.intl.useTranslator();
  const textTranslate = utils.app.textTranslated;
  const dispatch = useDispatch();
  const saveDocument = utils.hooks.useSaveDocument(false, 'modal');
  const { checkDocuments } = useCheckBeforeAction(actionType.SANITY_CHECK);
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const documentHeader = useSelector(selectors.documents.selectDocumentHeader);
  const { setStatus } = useFormikContext<{ [key: string]: Q1Value | FormValues }>() || {};
  const programCode = useSelector(selectors.app.getProgramCode);
  const getProgramCode = (idProgram: number) => {
    return lucyModuleKey[idProgram as keyof typeof lucyModuleKey] || '';
  };
  const idProgram = useSelector(selectors.app.getProgramCode);
  const history = useHistory();
  const validateLease = async () => {
    if (!protocol) return;
    const body = utils.documents.buildLeaseValidationRequest(protocol, idProgram, 'validate');

    try {
      const {
        data: {
          store: { RESPONSE },
        },
      } = await services.leaseValidate(body, 'modal');
      utils.documents.handlePayloadUpdate(RESPONSE, protocol, dispatch);

      dispatch(actions.modal.closeModal());
      dispatch(actions.documents.setActiveDocumentId([]));
      history.goBack();
      utils.app.notify('success', t('validate_lease'), 10000);
    } catch (e) {
      utils.app.notify('fail', t('error-validate_lease'), 10000);
      dispatch(actions.modal.closeModal());
    }
  };

  const applyRules = async () => {
    try {
      if (protocol) {
        const lucyModule = getProgramCode(programCode);
        const { data } = await services.checkMandatoryFields({ protocol, rulesGroup: lucyModule }, 'modal');
        const status = data.fields.map((el: any) => ({ idField: el.idField, status: el.fieldStatus }));
        setStatus(status);
        if (data.docStatus === 1) {
          utils.app.notify(
            'fail',
            textTranslate(data.docMessageStatus, t) || t('error-submitting-the-document'),
            10000,
          );
          data.docMessageStatusDetails?.length
            ? dispatch(
                actions.modal.openModal(modalActionType.documents.SHOW_RESPONSE, {
                  title: 'Status Details',
                  content: data.docMessageStatusDetails,
                }),
              )
            : dispatch(actions.modal.closeModal());
          return;
        } else if (data.docStatus === 2) {
          dispatch(
            actions.modal.openModal(modalActionType.documents.VALIDATION_CONFIRM_MODAL, {
              title: 'validate_lease_confirmation',
              subTitle: 'confirm_apply_rules_validate_lease',
              onConfirm: () => {
                validateLease();
                dispatch(actions.modal.closeModal());
              },
            }),
          );
          return;
        }
        validateLease();
      }
    } catch (e) {
      utils.app.notify('fail', textTranslate('error-submitting-the-document', t), 10000);
      throw e;
    }
  };

  const onValidateClick = async () => {
    try {
      const docFree = await checkDocuments();
      if (!docFree) return;
      const val = await saveDocument();
      if (val === null || val === undefined) {
        return;
      }
      applyRules();
    } catch (error) {
      dispatch(actions.modal.closeModal());
    }
  };

  const isValidateDisabled = documentHeader?.showUndoValidate || documentHeader?.showUndoValidate === null;

  return (
    <CommonButton
      id="validate-lease-btn"
      action={() =>
        dispatch(
          actions.modal.openModal(modalActionType.documents.VALIDATION_CONFIRM_MODAL, {
            title: 'validate_lease_confirmation',
            subTitle: 'once_validated_no_changes',
            onConfirm: onValidateClick,
          }),
        )
      }
      scope="tertiary"
      value={t('validate-lease')}
      customIcon="check"
      disabled={!utils.user.isActionEnabled(records, actionType.LEASE_VALIDATE) || isValidateDisabled}
    />
  );
};

export default ValidateLease;
